/**
 * @fileoverview Hook for fetching and managing user credit balance.
 * Listens for 'credits:changed' events to auto-refresh after credit-consuming operations.
 */

import { useState, useEffect, useCallback } from 'react';

interface CreditBalance {
  creditBalance: number;
  creditsUsedThisMonth: number;
  creditsPerMonth: number;
  maxRollover: number;
  lastCreditRefresh: string | null;
  tier: string;
}

/** Dispatch this event from anywhere to trigger a credit balance refresh */
export function notifyCreditChange() {
  window.dispatchEvent(new Event('credits:changed'));
}

export function useCredits() {
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/billing/credits', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setCredits(data);
        }
      }
    } catch {
      // Silently fail — credits display is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredits();

    const handler = () => fetchCredits();
    window.addEventListener('credits:changed', handler);
    return () => window.removeEventListener('credits:changed', handler);
  }, [fetchCredits]);

  return { credits, loading, refetchCredits: fetchCredits };
}
